/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, takeEvery, put, select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Time,
  ActionRequest,
  ActionSuccess,
  Recording,
  ActionFail,
  XError,
  TimeUtils,
  Constants,
  RecordingsApi,
  RecordingsConfigs,
  Asset,
  RecordingStatus,
  RecordingsUtils,
  Specs,
  RecordingsErrors,
  ParentalControlSettings,
  LEGACY,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionType {
  LOAD = 'recordings/single/LOAD',
  LOAD_BY_SERIES_ID = 'recordings/single/LOAD_BY_SERIES_ID',
  UPDATE = 'recordings/single/UPDATE',
  FAIL = 'recordings/single/FAIL',
  RESET = 'recordings/single/RESET',
}

interface RequestMeta {
  nowDate?: Time;
}

type Request = ActionRequest<ActionType.LOAD, RequestMeta>;
type LoadBySeriesIdsRequest = ActionRequest<ActionType.LOAD_BY_SERIES_ID, string[]>;
type Update = ActionSuccess<ActionType.UPDATE, Recording[]>;
type Fail = ActionFail<ActionType.FAIL>;
type Reset = ActionSuccess<ActionType.RESET>;

type Actions = Request | Update | Fail | LoadBySeriesIdsRequest | Reset;

const Actions = {
  load: (nowDate?: Time): Request => ({
    type: ActionType.LOAD, // STAY
    meta: {
      nowDate,
    },
  }),
  update: (payload: Recording[]): Update => ({
    type: ActionType.UPDATE,
    payload,
  }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
  loadBySeriesIds: (seriesIds: string[]): LoadBySeriesIdsRequest => ({
    type: ActionType.LOAD_BY_SERIES_ID,
    meta: seriesIds,
  }),
  reset: (): Reset => ({
    type: ActionType.RESET,
  }),
};

export const ActionCreators = {
  load: (nowDate?: Time): Request => Actions.load(nowDate),
  update: (payload: Recording[]): Update => Actions.update(payload),
  loadBySeriesIds: (seriesIds: string[]): LoadBySeriesIdsRequest => Actions.loadBySeriesIds(seriesIds),
  reset: (): Reset => Actions.reset(),
};

export interface ReducerState {
  data: Recording[];
  error?: XError;
}

const initialState: ReducerState = {
  data: [],
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.UPDATE: {
      return {
        data: action.payload,
      };
    }
    case ActionType.FAIL:
      return {
        ...state,
        error: action.error,
      };
    case ActionType.RESET:
      return initialState;
    case ActionType.LOAD:
    case ActionType.LOAD_BY_SERIES_ID:
    default:
      return state;
  }
}

// SELECTORS;
const getNamespace = (status: LEGACY.RecordingStatusValue): string => {
  return `single_recordings_by_status_${status}`;
};

export const Selectors = {
  getRecordings: (state: StoreState): Recording[] => {
    return state.recordings.single.data;
  },
  getError: (state: StoreState): XError | undefined => state.recordings.single.error,
};

function* getRecordingsApiCall(
  status: LEGACY.RecordingStatusValue,
  pageSize: number,
  nowDate?: Time,
  version?: LEGACY.RecordingsVersionValue
): SagaIterator<Recording[]> {
  const nowDate30DaysAgo: Time | undefined = nowDate
    ? TimeUtils.subtractDuration(nowDate, Constants.RECORDINGS_WINDOW_PAST_PERIOD)
    : undefined;

  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  if (!nowDate30DaysAgo) {
    // GET ALL
    yield Ducks.Pagination.clearPaginatedApi(getNamespace(status));
    return yield Ducks.Pagination.callCompletedPaginatedApi(
      getNamespace(status),
      pageSize,
      RecordingsApi.getSingleRecordingsByStatus,
      status,
      parentalControlSettings,
      version
    );
  }

  // UPDATE LAST 30 DAYS
  let recordingsToReturn: Recording[] = [];
  const localRecordings: Recording[] = yield select(Selectors.getRecordings);

  const recordingsIdsAfterLast30Days: number[] = localRecordings
    .filter((e) => e.status.toLowerCase() === status.toLowerCase() && TimeUtils.isAfter(e.startDate, nowDate30DaysAgo))
    .map((e) => e.recordingId);

  if (recordingsIdsAfterLast30Days.length > 0) {
    const updatedRecordings: Recording[] = yield Ducks.Api.callApi(
      RecordingsApi.getSingleRecordingsByRecordingIds,
      recordingsIdsAfterLast30Days,
      parentalControlSettings,
      version
    );

    const recordingsIdsBeforeLast30Days = localRecordings.filter(
      (e) => e.status.toLowerCase() === status.toLowerCase() && TimeUtils.isBefore(e.startDate, nowDate30DaysAgo)
    );

    recordingsToReturn = [...recordingsIdsBeforeLast30Days, ...updatedRecordings];
  } else {
    recordingsToReturn = localRecordings;
  }

  return recordingsToReturn;
}

function* loadSingleRecordingsSaga(action: Request): SagaIterator {
  try {
    const { nowDate } = action.meta;
    const { npvrVersion, pageSize }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);

    const results: Recording[][] = yield all([
      call(getRecordingsApiCall, LEGACY.RecordingStatusValue.COMPLETED, pageSize, nowDate, npvrVersion),
      call(getRecordingsApiCall, LEGACY.RecordingStatusValue.ON_GOING, pageSize, nowDate, npvrVersion),
      call(getRecordingsApiCall, LEGACY.RecordingStatusValue.SCHEDULED, pageSize, nowDate, npvrVersion),
    ]);

    // Store all recordings together since the backend state is not relevant.
    // The recordings state has to be calculated when needed by checking its start or end date
    const allResults = results.reduce((prev, curr) => {
      return [...prev, ...curr];
    }, []);

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
    yield put(Actions.update([...allResults, ...storedRecordings]));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* addSingleRecordingSaga(asset: Asset): SagaIterator<XError | undefined> {
  try {
    const { npvrVersion }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);

    const { recordingID }: LEGACY.RecordAssetResponse = yield Ducks.Api.callApi(
      RecordingsApi.addSingleRecording,
      asset
    );
    const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

    if (!Number.isNaN(recordingID)) {
      const recordings: Recording[] = yield Ducks.Api.callApi(
        RecordingsApi.getSingleRecordingsByRecordingIds,
        [Number(recordingID)],
        parentalControlSettings,
        npvrVersion
      );

      const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
      yield put(Actions.update([...recordings, ...storedRecordings]));
      return undefined;
    }
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* cancelSingleRecordingSaga(recording: Recording): SagaIterator<XError | undefined> {
  try {
    yield Ducks.Api.callApi(RecordingsApi.cancelSingleRecording, recording);

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
    const filteredRecordings = storedRecordings.filter((e) => e.internalId !== recording.internalId);

    yield put(Actions.update(filteredRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* deleteSingleRecordingSaga(recording: Recording): SagaIterator<XError | undefined> {
  try {
    yield Ducks.Api.callApi(RecordingsApi.deleteSingleRecording, recording);

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
    const filteredRecordings = storedRecordings.filter((e) => e.internalId !== recording.internalId);

    yield put(Actions.update(filteredRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

function* deleteBySeriesIdSaga(seriesId: string, channelEpgId: number): SagaIterator {
  try {
    yield Ducks.Api.callApi(RecordingsApi.deleteRecordingBySeriesId, seriesId, channelEpgId);

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
    const filteredRecordings = storedRecordings.filter(
      (e) => !(e.seriesId === seriesId && e.channelEpgId === channelEpgId)
    );
    yield put(Actions.update(filteredRecordings));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* deleteBySeasonSaga(
  seriesId: string,
  channelEpgId: number,
  seasonNumber: number
): SagaIterator<XError | undefined> {
  try {
    yield Ducks.Api.callApi(RecordingsApi.deleteRecordingBySeason, seriesId, channelEpgId, seasonNumber);

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
    const filteredRecordings = storedRecordings.filter(
      (e) => !(e.seriesId === seriesId && e.channelEpgId === channelEpgId && e.seasonNumber === seasonNumber)
    );

    yield put(Actions.update(filteredRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* deleteByStatusSaga(
  seriesId: string,
  channelEpgId: number,
  recordingStatus: RecordingStatus[],
  seasonNumber?: number
): SagaIterator<XError | undefined> {
  try {
    yield Ducks.Api.callApi(
      RecordingsApi.deleteRecordingByStatus,
      recordingStatus,
      seriesId,
      channelEpgId,
      seasonNumber
    );

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);

    const filteredRecordings = storedRecordings.filter(
      (e) =>
        !(
          e.seriesId === seriesId &&
          e.channelEpgId === channelEpgId &&
          recordingStatus.includes(RecordingsUtils.getRecordingStatus(e)) &&
          (!seasonNumber || seasonNumber === e.seasonNumber)
        )
    );

    yield put(Actions.update(filteredRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export function* changeProtectedStatusSaga(recording: Recording, isProtect: boolean): SagaIterator<XError | undefined> {
  try {
    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);

    if (isProtect) {
      const protectedRecordings = storedRecordings.filter((e) => e.isAssetProtected);
      if (protectedRecordings.length >= Specs.RECORDING_OPTION_MAX_PROTECTED_ITEMS) {
        return XError.fromCode(RecordingsErrors.validation.protectedLimitExcedded);
      }
    }

    yield Ducks.Api.callApi(RecordingsApi.updateSingleRecordingProtectionStatus, recording, isProtect);

    storedRecordings.forEach((e) => {
      if (e.internalId === recording.internalId) {
        e.isAssetProtected = isProtect;
      }
    });

    yield put(Actions.update(storedRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

function* loadBySeriesIdsSaga(action: LoadBySeriesIdsRequest): SagaIterator {
  try {
    const seriesIds = action.meta;

    const { npvrVersion }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);
    const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

    const newRecordings: Recording[] = yield Ducks.Api.callApi(
      RecordingsApi.getSingleRecordingsBySeriesIDs,
      seriesIds,
      parentalControlSettings,
      npvrVersion
    );

    const storedRecordings: Recording[] = yield select(Selectors.getRecordings);

    const filteredStoredRecordings = storedRecordings.filter(
      (e) => newRecordings.find((r) => e.recordingId === r.recordingId) === undefined
    );

    yield put(Actions.update([...newRecordings, ...filteredStoredRecordings]));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* deleteCompletedUnprotectedRecordingsSaga(
  seriesId: string,
  channelEpgId: number,
  seasonNumber?: number
): SagaIterator<XError | undefined> {
  const storedRecordings: Recording[] = yield select(Selectors.getRecordings);
  try {
    const toDeleteRecordings: Recording[] = [];
    const toUpdateRecordings: Recording[] = [];

    storedRecordings.forEach((e) => {
      if (
        e.seriesId === seriesId &&
        e.channelEpgId === channelEpgId &&
        RecordingsUtils.getRecordingStatus(e) === RecordingStatus.COMPLETED &&
        !e.isAssetProtected &&
        (!seasonNumber || e.seasonNumber === seasonNumber)
      ) {
        toDeleteRecordings.push(e);
      } else {
        toUpdateRecordings.push(e);
      }
    });

    yield all(toDeleteRecordings.map((e) => Ducks.Api.callApi(RecordingsApi.deleteSingleRecording, e)));

    yield put(Actions.update(toUpdateRecordings));
    return undefined;
  } catch (error) {
    return XError.fromError(error);
  }
}

export const SagaFunctions = {
  changeProtectedStatusSaga,
  deleteByStatusSaga,
  deleteBySeasonSaga,
  addSingleRecordingSaga,
  cancelSingleRecordingSaga,
  deleteSingleRecordingSaga,
  deleteCompletedUnprotectedRecordingsSaga,
  deleteBySeriesIdSaga,
};

export function* saga(): SagaIterator {
  yield all([
    takeEvery(ActionType.LOAD, loadSingleRecordingsSaga),
    takeEvery(ActionType.LOAD_BY_SERIES_ID, loadBySeriesIdsSaga),
  ]);
}
